import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BedDouble, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function NurseDashboard() {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdmissions();
  }, []);

  const fetchAdmissions = async () => {
    try {
      const response = await api.get('/admissions');
      setAdmissions(response.data.filter(adm => adm.status === 'admitted'));
    } catch (error) {
      toast.error('Failed to fetch admissions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="nurse-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Nurse Dashboard
          </h1>
          <p className="text-slate-600 mt-2">Monitor admitted patients and vitals</p>
        </div>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              <BedDouble className="h-5 w-5 text-[#4A238F]" />
              Active Admissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {admissions.length === 0 ? (
              <p className="text-slate-500 text-sm">No active admissions</p>
            ) : (
              <div className="space-y-3">
                {admissions.map((admission) => (
                  <div key={admission.id} className="p-4 bg-slate-50 rounded-md border border-slate-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">Room {admission.room_number}</p>
                        <p className="text-sm text-slate-600 mt-1">{admission.admission_reason}</p>
                        {admission.notes && (
                          <p className="text-xs text-slate-500 mt-2">{admission.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                        <span className="text-sm text-emerald-600">Admitted</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}